import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, ActivityIndicator } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet } from '../../../src/api/client';
import { AdminToolbar } from '../../../src/components/AdminList';
import Card from '../../../src/components/Card';
import QrSvgView from '../../../src/components/QrSvgView';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import { downloadFile } from '../../../src/utils/fileExport';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface FleetAccount {
  id: string;
  companyName: string;
  status: string;
  /** This month (owner-local), discounted, net of refunds. */
  mtdRevenue: number;
}

interface FleetStats {
  totalAccounts: number;
  totalRevenue: number;
  /** This month (owner-local), discounted, net of refunds — matches the card label. */
  mtdRevenue: number;
}

interface FleetApplication { id: string; status: string }

/** FleetDashboardScreen - fleet accounts overview with MTD revenue. */
export default function FleetDashboardScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [accounts, setAccounts] = useState<FleetAccount[]>([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountsPage, setAccountsPage] = useState(1);
  const [accountsSearch, setAccountsSearch] = useState('');
  const [stats, setStats] = useState<FleetStats | null>(null);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const toast = useToast();
  const { isOffline } = useNetwork();

  const fetchAccounts = async (p = accountsPage, searchOverride?: string) => {
    const params = new URLSearchParams({ page: String(p), limit: '10' });
    const s = searchOverride ?? accountsSearch;
    if (s) params.set('search', s);
    const data = await apiGet<{ accounts: FleetAccount[]; total: number; stats: FleetStats }>(`/api/fleet/accounts?${params}`);
    setAccounts(data.accounts);
    setAccountsTotal(data.total);
    setStats(data.stats);
    setAccountsPage(p);
  };

  const fetchData = async () => {
    let pendingFailed = false;
    try {
      setLoading(true);
      const [, pending] = await Promise.all([
        fetchAccounts(),
        apiGet<FleetApplication[]>('/api/fleet/applications?status=pending').catch(() => { pendingFailed = true; return [] as FleetApplication[]; }),
      ]);
      setPendingApplications(pending.length);
      if (pendingFailed && !isOffline) {
        toast.show({ type: 'warning', title: 'Pending applications count didn’t load' });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  const fetchSignupQR = async () => {
    try {
      setQrLoading(true);
      const data = await apiGet<{ qrSvgDataUrl: string; signupUrl: string }>('/api/fleet/me/signup-qr');
      setQrSvg(data.qrSvgDataUrl);
      setQrUrl(data.signupUrl);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setQrLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return colors.success;
      case 'suspended': return colors.warning;
      case 'cancelled': return colors.danger;
      default: return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'checkmark-circle';
      case 'suspended': return 'pause-circle';
      case 'cancelled': return 'close-circle';
      default: return 'help-circle';
    }
  };

  if (loading && !stats) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}
    >
      <ContentContainer>
      {/* Summary Cards */}
      <View style={styles.summaryRow}>
        <Card style={styles.summaryCard}>
          <View style={styles.summaryIcon}>
            <Ionicons name="business" size={24} color={colors.primary} />
          </View>
          <Text style={styles.summaryValue}>{stats?.totalAccounts || 0}</Text>
          <Text style={styles.summaryLabel}>Fleet Accounts</Text>
        </Card>
      </View>

      <Card style={styles.revenueCard}>
        <View style={styles.revenueIcon}>
          <Ionicons name="cash" size={28} color={colors.success} />
        </View>
        <Text style={styles.revenueValue}>{fmt(stats?.mtdRevenue || 0)}</Text>
        <Text style={styles.revenueLabel}>MTD Revenue</Text>
      </Card>

      {/* Action Buttons */}
      <View style={styles.actionRow}>
        <Button
          title="Add Fleet Account"
          onPress={() => router.push('/owner/fleet/add-account' as never)}
          icon={<Ionicons name="add" size={20} color={colors.white} />}
          style={styles.actionButton}
        />
        <Button
          title="Pricing Tiers"
          onPress={() => router.push('/owner/fleet/tiers' as never)}
          variant="outline"
          icon={<Ionicons name="pricetag" size={20} color={colors.primary} />}
          style={styles.actionButton}
        />
      </View>

      <View style={styles.actionRow}>
        <Button
          title={`Pending Applications${pendingApplications ? ` (${pendingApplications})` : ''}`}
          onPress={() => router.push('/owner/fleet/applications' as never)}
          variant={pendingApplications ? 'primary' : 'outline'}
          icon={<Ionicons name="mail-unread" size={20} color={pendingApplications ? colors.white : colors.primary} />}
          style={styles.actionButton}
        />
        <Button
          title={qrSvg ? 'Refresh QR' : 'Print Signup QR'}
          onPress={fetchSignupQR}
          variant="outline"
          loading={qrLoading}
          icon={<Ionicons name="qr-code" size={20} color={colors.primary} />}
          style={styles.actionButton}
        />
      </View>

      {qrSvg ? (
        <Card style={{ alignItems: 'center', marginBottom: spacing.lg }}>
          <Text style={{ fontWeight: fontWeight.semibold, color: colors.text, marginBottom: spacing.sm }}>Fleet Signup QR</Text>
          {/* SVG data URL: RN <Image> renders it on web but blank on native — use QrSvgView. */}
          <QrSvgView uri={qrSvg} width={280} accessibilityLabel="Fleet signup QR" />
          {qrUrl ? <Text selectable style={{ marginTop: spacing.sm, color: colors.textSecondary, fontSize: fontSize.xs }}>{qrUrl}</Text> : null}
          <Button
            title="Download"
            onPress={() => { if (qrSvg) downloadFile(qrSvg, 'fleet-signup-qr.svg').catch(() => {}); }}
            variant="outline"
            size="sm"
            style={{ marginTop: spacing.sm }}
          />
        </Card>
      ) : null}

      {/* Fleet Accounts List */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Fleet Accounts</Text>
      </View>

      <AdminToolbar
        search={accountsSearch}
        onSearchChange={setAccountsSearch}
        onSearch={() => fetchAccounts(1).catch(() => {})}
        total={accountsTotal}
        page={accountsPage}
        limit={10}
        onPageChange={(p) => fetchAccounts(p).catch(() => {})}
        csvEndpoint={`/api/fleet/accounts${accountsSearch ? `?search=${encodeURIComponent(accountsSearch)}` : ''}`}
        csvFilename="fleet-accounts.csv"
        label="account"
      />

      {accounts.length > 0 ? (
        accounts.map(account => (
          <TouchableOpacity
            key={account.id}
            onPress={() => router.push(`/owner/fleet/${account.id}` as never)}
          >
            <Card style={styles.accountCard}>
              <View style={styles.accountRow}>
                <View style={styles.accountIcon}>
                  <Ionicons name="business" size={24} color={colors.primary} />
                </View>
                <View style={styles.accountInfo}>
                  <Text style={styles.accountName}>{account.companyName}</Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
              </View>

              <View style={styles.accountFooter}>
                <View style={styles.accountStat}>
                  <Ionicons name="cash" size={16} color={colors.success} />
                  <Text style={[styles.accountStatText, { color: colors.success, fontWeight: fontWeight.semibold }]}>
                    {fmt(Number(account.mtdRevenue) || 0)} MTD
                  </Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(account.status) + '20' }]}>
                  <Ionicons name={getStatusIcon(account.status) as keyof typeof Ionicons.glyphMap} size={14} color={getStatusColor(account.status)} />
                  <Text style={[styles.statusText, { color: getStatusColor(account.status) }]}>
                    {account.status}
                  </Text>
                </View>
              </View>
            </Card>
          </TouchableOpacity>
        ))
      ) : (
        <Card style={styles.emptyCard}>
          <Ionicons name="business-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>{accountsSearch.trim() ? 'No accounts found' : 'No fleet accounts yet'}</Text>
          <Text style={styles.emptyText}>{accountsSearch.trim()
            ? 'No fleet accounts match your search'
            : 'Add your first fleet account to start managing commercial customers'}</Text>
        </Card>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  summaryRow: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.md },
  summaryCard: { flex: 1, alignItems: 'center', paddingVertical: spacing.lg },
  summaryIcon: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center', marginBottom: spacing.sm,
  },
  summaryValue: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text },
  summaryLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: spacing.xs },

  revenueCard: { alignItems: 'center', paddingVertical: spacing.lg, marginBottom: spacing.lg },
  revenueIcon: {
    width: 56, height: 56, borderRadius: 28,
    backgroundColor: c.success + '20',
    justifyContent: 'center', alignItems: 'center', marginBottom: spacing.sm,
  },
  revenueValue: { fontSize: fontSize.hero, fontWeight: fontWeight.bold, color: c.success },
  revenueLabel: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },

  actionRow: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.lg },
  actionButton: { flex: 1 },

  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },

  accountCard: { marginBottom: spacing.md },
  accountRow: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md },
  accountIcon: {
    width: 48, height: 48, borderRadius: 24,
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center', marginRight: spacing.md,
  },
  accountInfo: { flex: 1 },
  accountName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  accountType: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },

  accountFooter: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight,
  },
  accountStat: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  accountStatText: { fontSize: fontSize.sm, color: c.textSecondary },

  statusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.sm,
  },
  statusText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium },

  emptyCard: { alignItems: 'center', paddingVertical: spacing.xxl },
  emptyTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: 280 },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
